import { OverlayModule } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  ViewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
  EmptyStateComponent,
  StatusLabelComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  DialogPresets,
  extendSortingDataAccessor,
  NgCommonModule,
  ObservableDataSource,
  timestampSortingAccessor,
} from '@principle-theorem/ng-shared';
import { FundingRequest, Invoice } from '@principle-theorem/principle-core';
import {
  FundingRequestStatus,
  IAccInvoiceFundingRequest,
  IBrand,
  IInvoice,
  IPatient,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  DATE_TIME_FORMAT,
  DocumentReference,
  Firestore,
  HISTORY_DATE_FORMAT,
  INamedDocument,
  WithRef,
} from '@principle-theorem/shared';
import { compact, first } from 'lodash';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccApiService } from '../../../acc/acc-api.service';
import { AccInvoiceService } from '../../../acc/acc-invoice.service';
import {
  AccViewInvoiceDialogComponent,
  IAccViewInvoiceDialogData,
} from '../acc-view-invoice-dialog/acc-view-invoice-dialog.component';
import { AccTestChipComponent } from '../acc-test-chip/acc-test-chip.component';

@Component({
  selector: 'pr-acc-invoices-table',
  imports: [
    EmptyStateComponent,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    NgCommonModule,
    OverlayModule,
    RouterModule,
    StatusLabelComponent,
    AccTestChipComponent,
  ],
  templateUrl: './acc-invoices-table.component.html',
  styleUrl: './acc-invoices-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AccInvoiceService],
})
export class AccInvoicesTableComponent {
  private _dialog = inject(MatDialog);
  accApi = inject(AccApiService);
  practice = input.required<WithRef<IPractice>>();
  brand = input.required<WithRef<IBrand>>();
  patient = input<WithRef<IPatient> | undefined>();
  accInvoiceService = inject(AccInvoiceService);
  invoiceRequests$: Observable<WithRef<IAccInvoiceFundingRequest>[]>;
  dataSource: ObservableDataSource<WithRef<IAccInvoiceFundingRequest>>;
  noInvoices$: Observable<boolean>;
  displayedColumns = computed(() =>
    compact([
      'status',
      'createdAt',
      'invoiceId',
      'claimNumber',
      !this.patient() ? 'patient' : undefined,
      'invoiceLink',
      'syncStatus',
      'actions',
    ])
  );

  readonly pageSizeOptions = [25, 50, 100];
  readonly pageSize = 25;
  readonly dateFormat = HISTORY_DATE_FORMAT;
  readonly dateTimeFormat = DATE_TIME_FORMAT;

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  @ViewChild(MatPaginator)
  set tablePaginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  constructor() {
    this.invoiceRequests$ = this.accInvoiceService.getAccInvoices$(
      toObservable(this.patient)
    );

    this.dataSource = new ObservableDataSource(this.invoiceRequests$);
    this.dataSource.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );
    this.noInvoices$ = this.dataSource.filteredLength$.pipe(
      map((length) => length === 0)
    );
  }

  getStatusColour(status: FundingRequestStatus): string {
    return FundingRequest.getStatusColour(status);
  }

  getPatientLink(patient: INamedDocument<IPatient>): string[] {
    return ['/', this.brand().slug, 'patients', patient.ref.id];
  }

  getInvoiceLink(invoice: WithRef<IInvoice>): string[] {
    return [
      '/',
      this.brand().slug,
      'patients',
      Invoice.patientRef(invoice).id,
      'account',
      'invoices',
      invoice.ref.id,
    ];
  }

  getInvoice(
    invoiceRef: DocumentReference<IInvoice>
  ): Promise<WithRef<IInvoice>> {
    return Firestore.getDoc(invoiceRef);
  }

  getClaimNumber(invoiceRequest: WithRef<IAccInvoiceFundingRequest>): string {
    return first(invoiceRequest.data.invoice?.lines)?.claimId ?? '';
  }

  deleteDisabled(invoiceRequest: WithRef<IAccInvoiceFundingRequest>): boolean {
    return (
      invoiceRequest.status !== FundingRequestStatus.Draft &&
      !invoiceRequest.data.transactions?.length
    );
  }

  viewInvoiceDetails(invoiceRequest: WithRef<IAccInvoiceFundingRequest>): void {
    if (!invoiceRequest.data.invoice) {
      return;
    }
    const data: IAccViewInvoiceDialogData = {
      accInvoice: invoiceRequest.data.invoice,
      invoiceRequest,
    };
    this._dialog.open<AccViewInvoiceDialogComponent, IAccViewInvoiceDialogData>(
      AccViewInvoiceDialogComponent,
      DialogPresets.medium({ data })
    );
  }

  invoiceLink$(invoiceRef: DocumentReference<IInvoice>): Observable<string[]> {
    const patientRef = Invoice.patientRef({ ref: invoiceRef });
    return of([
      '/',
      this.brand().slug,
      'patients',
      patientRef.id,
      'account',
      'invoices',
      invoiceRef.id,
    ]);
  }

  private _sortingDataAccessor(
    data: WithRef<IAccInvoiceFundingRequest>,
    sortHeaderId: string
  ): string | number | undefined {
    switch (sortHeaderId) {
      case 'createdAt':
        return timestampSortingAccessor(data.createdAt);
      default:
        return;
    }
  }
}
